import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeftIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { VideoCameraSlashIcon } from '@heroicons/react/24/solid';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import { FaYoutube } from 'react-icons/fa';
import { useFormContext } from "react-hook-form";
import { IoIosVideocam } from 'react-icons/io';
import useApiUrlTranslation from './useApiUrlTranslation';
import HTMLReactParser from 'html-react-parser';
import RegistrationFormRow from './RegistrationFormRow';
import { useParams } from 'react-router-dom';

const YoutubePreview = ({ url }) => {
    const extractYoutubeId = (source) => {
        if (!source) return null;
        const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        const match = source.match(regex);
        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    };

    const videoSrc = extractYoutubeId(url);

    if (!videoSrc) {
        return null; // Ne rien afficher si l'URL est invalide ou absente
    }

    return (
        <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
            <iframe
                src={videoSrc}
                title="YouTube video player"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            ></iframe>
        </div>
    );
};



// Modified IllustrationPreview to accept a map of active inputs
const IllustrationPreview = ({ activeInputsMap,
    type = "video",
    presta, inputName }) => {



    const extractYoutubeId = (source) => {
        if (!source) return null;
        const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        const match = source.match(regex);
        if (!match) return null;
        let url = `https://www.youtube.com/embed/${match[1]}`;
        return url.replace(/&t=\d+/, '');
    }

    // Conteneur avec ratio d'aspect pour tous les aperçus
    const previewContainer = (children) => (
        <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
            {children}
        </div>
    );

    // Priorité 1: Afficher la prévisualisation du fichier en cours de sélection
    const inputToPreview = activeInputsMap[inputName];
    if (inputToPreview) {
        const { name, detectedType, value } = inputToPreview;

        if (['video_url', 'contribution', 'organisme_video_url'].includes(name) && value) {
            return previewContainer(
                <iframe src={extractYoutubeId(value)} className="w-full h-full" allowFullScreen></iframe>
            );
        }
        if (detectedType?.startsWith('image/') && value) {
            return previewContainer(
                <img src={value} alt="Preview" className="w-full h-full object-cover" />
            );
        }
        if (detectedType?.startsWith('video/') && value) {
            return previewContainer(
                <video controls className="w-full h-full object-cover">
                    <source src={value} type={detectedType} />
                    Your browser does not support the video tag.
                </video>
            );
        }
    }

    // Priorité 2: Si aucune prévisualisation active, afficher les données existantes de `presta`
    if (presta) {
        if (inputName === "video_hosted" && presta.video_hosted) {
            return previewContainer(
                <video controls className="w-full h-full object-cover">
                    <source src={`https://www.mlg-consulting.com/manager_cc/docs/archives/${presta.video_hosted}`} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        }

        if (inputName === "video_url" && presta.video_url) {
            const youtubeSrc = extractYoutubeId(presta.video_url);
            if (youtubeSrc) {
                return previewContainer(
                    <iframe
                        title='YouTube video player'
                        src={youtubeSrc}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen>
                    </iframe>
                );
            }
        }

        if (inputName === "presta_visuel" && presta.presta_visuel_thumbs?.source) {
            return previewContainer(
                <img
                    src={presta.presta_visuel_thumbs.source}
                    alt="Presta Visual"
                    className="w-full h-full object-cover"
                />
            );
        }
    }

    return null; // Ne rien afficher si aucune source n'est disponible
}



const PrestaIllustrationSectionCopy = ({ handleInputChange,
    inputVideoUrlName = "contribution",
    inputVideoHostedName = "file_src",
    inputPrestaVisuelName = "file_src",
    presta,
}) => {

    const { t, i18n, apiUrlTranslation } = useApiUrlTranslation();

    const { idEvent } = useParams();

    const { register, setValue, watch, formState } = useFormContext();

    const is2269IT = idEvent === "2269" && i18n.resolvedLanguage === "it";
    const is2269EN = idEvent === "2269" && i18n.resolvedLanguage === "en";
    const is2269FR = idEvent === "2269" && i18n.resolvedLanguage === "fr";

    const isVideoHostedRequired = idEvent === "2269" && !presta?.video_hosted;

    const videoLabel = is2269IT
        ? `Carica un video di presentazione <span class="text-green-700 font-bold">(consigliato)</span>`
        : is2269EN
            ? `Upload a presentation video <span class="text-green-700 font-bold">(recommended)</span>`
            : is2269FR
                ? `Téléchargez une vidéo de votre innovation/produit <span class="text-green-700 font-bold">(recommandé)</span>`
                : t('form.sections.PrestaIllustrationSection.video_illustration');

    const videoSubtext = is2269IT
        ? "Il video verrà riprodotto sullo sfondo durante la registrazione dell'intervista."
        : is2269EN
            ? "This video will be played in the background during your interview recording."
            : is2269FR
                ? "Cette vidéo sera diffusée en arrière-plan lors de l'enregistrement de votre interview."
                : null;

    const videoFormat = is2269IT
        ? "Formati accettati: .mp4 o .mov"
        : is2269EN
            ? "Accepted formats: .mp4 or .mov"
            : "Format .mp4 ou .mov :";


    // --- NOUVELLE LOGIQUE DE PREVISUALISATION ---
    const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [videoFileName, setVideoFileName] = useState('');

    const watchedVideoFile = watch(inputVideoHostedName);
    const watchedImageFile = watch(inputPrestaVisuelName);
    const watchedVideoUrl = watch(inputVideoUrlName);

    useEffect(() => {
        if (watchedVideoFile && watchedVideoFile.length > 0) {
            const file = watchedVideoFile[0];
            const limitMB = 100;
            const limitBytes = limitMB * 1024 * 1024;

            if (file.size > limitBytes) {
                alert(t('form.alerts.file_too_large', { maxSize: limitMB }));
                setValue(inputVideoHostedName, null);
                setVideoPreviewUrl(null);
                return;
            }
            const newUrl = URL.createObjectURL(file);
            setVideoPreviewUrl(newUrl);

            // Nettoyage de l'URL précédente pour éviter les fuites de mémoire
            return () => URL.revokeObjectURL(newUrl);
        }
    }, [watchedVideoFile, setValue, inputVideoHostedName, t]);

    useEffect(() => {
        if (presta?.video_url && !watchedVideoUrl) { // Set default value only if user hasn't typed anything
            setValue(inputVideoUrlName, presta.video_url);
        }
    }, [presta, inputVideoUrlName, setValue, watchedVideoUrl]);


    useEffect(() => {
        if (watchedImageFile && watchedImageFile.length > 0) {
            const file = watchedImageFile[0];
            const limitMB = 5;
            const limitBytes = limitMB * 1024 * 1024;

            if (file.size > limitBytes) {
                alert(t('form.file_too_large', { maxSize: limitMB }));
                setValue(inputPrestaVisuelName, null);
                setImagePreviewUrl(null);
                return;
            }

            const newUrl = URL.createObjectURL(file);
            setImagePreviewUrl(newUrl);

            // Nettoyage de l'URL précédente pour éviter les fuites de mémoire
            return () => URL.revokeObjectURL(newUrl);
        }
    }, [watchedImageFile, setValue, inputPrestaVisuelName, t]);
    // --- FIN DE LA NOUVELLE LOGIQUE ---

    const videoHostedRegProps = register(inputVideoHostedName, {
        required: isVideoHostedRequired ? t('form.alerts.veuillez_renseigner') : false,
    });


    useEffect(() => {
        if (presta?.video_url && inputVideoUrlName === "video_url") {
            setValue(inputVideoUrlName, presta.video_url);
        }
    }, [presta, inputVideoUrlName, setValue]);


    // Cette fonction n'est plus nécessaire pour la validation des fichiers, 
    // mais peut être conservée si handleInputChange est utilisé ailleurs.
    const handleFileChange = (e) => {
        if (handleInputChange) {
            handleInputChange(e);
        }
    };
    return <div>
        <div className="flex mt-3   pt-3  ">
            <div className="basis-3/12 font-bold">
                {HTMLReactParser(videoLabel)}
                {isVideoHostedRequired && <span className="p-2 text-red-600 dark:text-red-400">*</span>}
                {videoSubtext && <p className="font-normal text-sm mt-1">{videoSubtext}</p>}
                <br /> {videoFormat}</div>

            <div className="basis-9/12 border">

                <div className="hidden flex space-x-2 mb-3 justify-between ">
                    <div className="bg-neutral-50 p-2 rounded mb-2 w-100 basis-10/12">
                        {(HTMLReactParser(t('form.sections.PrestaIllustrationSection.video_illustration_option_1')))}
                        <input
                            {...register(inputVideoUrlName)}

                            className="border mt-3 p-2 block w-full rounded-full border-gray-700 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            type="text"
                            placeholder="Ex: https://www.youtube.com/watch?v=your_video_id"
                        />
                    </div>
                    <div className="basis-2/12 flex justify-center items-center">

                        <YoutubePreview url={watchedVideoUrl || presta?.video_url} />
                    </div>
                </div>
                <div className="flex space-x-2 mb-3 justify-between">
                    <div className="bg-neutral-50 p-2 rounded w-100 basis-10/12">
                        <div className="mb-1">
                            {/* {HTMLReactParser(t('form.sections.PrestaIllustrationSection.video_illustration_option_2'))}
                    */}     </div>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                id="input-file-video-hosted"
                                {...videoHostedRegProps}
                                type="file"
                                className="sr-only"
                                accept="video/mp4"
                                onChange={(e) => {
                                    videoHostedRegProps.onChange(e);
                                    setVideoFileName(e.target.files?.[0]?.name || '');
                                }}
                            />
                            <label
                                htmlFor="input-file-video-hosted"
                                className="cursor-pointer inline-flex items-center px-3 py-1.5 border border-gray-400 dark:border-neutral-500 rounded bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 text-sm text-gray-700 dark:text-neutral-200 transition-colors"
                            >
                                {t('form.parcourir')}
                            </label>
                            <span className="text-sm text-gray-500 dark:text-neutral-400 truncate max-w-[200px]">
                                {videoFileName || t('form.no_file_selected')}
                            </span>
                        </div>
                        {formState.errors[inputVideoHostedName] && (
                            <div className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2 mt-2">
                                {formState.errors[inputVideoHostedName].message || t('form.alerts.veuillez_renseigner')}
                            </div>
                        )}
                    </div>
                    <div className="basis-2/12 flex justify-center items-center">
                        {/* Affiche la prévisualisation de la vidéo si elle existe */}
                        {videoPreviewUrl ? (
                            <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
                                <video controls src={videoPreviewUrl} className="w-full h-full object-cover" />
                            </div>
                        ) : presta?.video_hosted ? (
                            <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
                                <video controls src={`https://www.mlg-consulting.com/manager_cc/docs/archives/${presta.video_hosted}`} className="w-full h-full object-cover" />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>

        {/* <div className="flex mt-3  border-t pt-3 border-neutral-200 items-center">
            <div className="basis-3/12 font-bold">
                {HTMLReactParser(t('form.sections.PrestaIllustrationSection.image_illustration'))}
                <span className="p-2 text-danger">*</span>
            </div>
            <div className="basis-9/12">
                <div className="flex">
                    <div className="basis-10/12">
                        <input

                            {...register(inputPrestaVisuelName, { required: !presta?.presta_visuel })}
                            className="mt-3 block w-full rounded-md  focus:border-indigo-500 focus:ring-indigo-500  "
                            type="file"
                            accept="image/png, image/jpeg"

                        />
                    </div>
                    <div className="basis-2/12 ">
                        {imagePreviewUrl ? (
                            <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
                                <img src={imagePreviewUrl} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                        ) : presta?.presta_visuel_thumbs?.source ? (
                            <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-md overflow-hidden">
                                <img src={presta.presta_visuel_thumbs.source} alt="Visual" className="w-full h-full object-cover" />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div> */}
    </div>
};

export default PrestaIllustrationSectionCopy;